<template>
  <v-card id="operator-report">
    <v-card-title class="text-h6">
      Отчёт по операторам
    </v-card-title>

    <v-card-text class="pb-0">
      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="filters.from"
            type="datetime-local"
            label="Период с"
            dense
            outlined
            hide-details
            :error="!filters.from"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="filters.to"
            type="datetime-local"
            label="Период по"
            dense
            outlined
            hide-details
            :error="!filters.to"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.statusIds"
            :items="statusItems"
            :disabled="!loaded"
            :hint="loaded ? '' : 'Появятся после первой загрузки'"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            clearable
            persistent-hint
            label="Статусы заказа"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.restaurantId"
            :items="restaurantItems"
            :disabled="!loaded"
            :hint="loaded ? '' : 'Появятся после первой загрузки'"
            dense
            outlined
            clearable
            persistent-hint
            label="Филиал"
          ></v-select>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.expeditionType"
            :items="expeditionItems"
            dense
            outlined
            hide-details
            clearable
            label="Тип заказа"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.operatorName"
            :items="operators"
            :disabled="!loaded"
            :hint="loaded ? '' : 'Появятся после первой загрузки'"
            dense
            outlined
            clearable
            persistent-hint
            label="Оператор"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-select
            v-model="filters.source"
            :items="sources"
            :disabled="!loaded"
            :hint="loaded ? '' : 'Появятся после первой загрузки'"
            dense
            outlined
            clearable
            persistent-hint
            label="Источник заказа"
          ></v-select>
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="filters.query"
            dense
            outlined
            hide-details
            clearable
            label="Поиск в Delivery"
            hint="Ищет сам Delivery"
          ></v-text-field>
        </v-col>

        <v-col
          cols="12"
          md="3"
          class="d-flex align-center"
        >
          <v-switch
            v-model="filters.isLate"
            dense
            hide-details
            class="mt-0"
            label="Только опоздавшие"
          ></v-switch>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text>
      <div class="d-flex align-center flex-wrap">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!periodChosen"
          @click="refresh"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          <span>Обновить</span>
        </v-btn>

        <v-btn
          outlined
          class="ms-3"
          :loading="downloading"
          :disabled="!loaded || !periodChosen"
          @click="download"
        >
          <v-icon
            size="18"
            class="me-2"
          >
            {{ icons.mdiExportVariant }}
          </v-icon>
          <span>Выгрузить отчёт</span>
        </v-btn>

        <v-spacer></v-spacer>

        <span
          v-if="loaded"
          class="text--secondary"
        >
          Заказов: {{ totals.ordersCount }}
        </span>
      </div>

      <v-alert
        v-if="error"
        type="error"
        dense
        text
        class="mt-4 mb-0"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="truncated"
        type="warning"
        dense
        text
        class="mt-4 mb-0"
      >
        Заказов за период больше, чем Delivery отдаёт за раз — отчёт неполный. Сузьте период.
      </v-alert>
    </v-card-text>

    <v-tabs v-model="tab">
      <v-tab>Развёрнутый отчёт</v-tab>
      <v-tab>Сводный отчёт</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card-text>
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            class="operator-report-search mb-4"
            label="Поиск по таблице"
            :prepend-inner-icon="icons.mdiMagnify"
          ></v-text-field>

          <v-data-table
            :headers="detailedHeaders"
            :items="rows"
            :search="search"
            :loading="loading"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
            class="text-no-wrap"
          >
            <template #[`item.createdAt`]="{item}">
              {{ formatDate(item.createdAt) }}
            </template>

            <template #[`item.orderAmount`]="{item}">
              {{ formatMoney(item.orderAmount) }}
            </template>

            <template #[`item.paidAmount`]="{item}">
              {{ formatMoney(item.paidAmount) }}
            </template>

            <template #[`body.append`]>
              <tr class="font-weight-bold">
                <td colspan="6">
                  ИТОГО с учётом фильтров
                </td>
                <td class="text-right">
                  {{ formatMoney(totals.orderAmount) }}
                </td>
                <td class="text-right">
                  {{ formatMoney(totals.paidAmount) }}
                </td>
                <td colspan="2"></td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-tab-item>

      <v-tab-item>
        <v-card-text>
          <v-data-table
            :headers="summaryHeaders"
            :items="summary"
            :loading="loading"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
            class="text-no-wrap"
          >
            <template #[`item.orderAmount`]="{item}">
              {{ formatMoney(item.orderAmount) }}
            </template>

            <template #[`item.paidAmount`]="{item}">
              {{ formatMoney(item.paidAmount) }}
            </template>

            <template #[`body.append`]>
              <tr class="font-weight-bold">
                <td>ИТОГО</td>
                <td class="text-right">
                  {{ summaryTotals.ordersCount }}
                </td>
                <td class="text-right">
                  {{ formatMoney(summaryTotals.orderAmount) }}
                </td>
                <td class="text-right">
                  {{ formatMoney(summaryTotals.paidAmount) }}
                </td>
              </tr>
            </template>
          </v-data-table>

          <p class="text--secondary mb-0 mt-2">
            «Фио оператора» — тот, кто заказ оформил. У заказов нашего приложения оформившего
            человека нет, программа их создаёт сама, поэтому там показан тот, кто первым взял
            заказ в работу.
            В сводку идёт только доставка, без выноса — и количество, и суммы.
            Строка «Без оператора» — заказы, у которых Delivery не назвал оператора: он пишет того,
            кто последним работал с заказом руками.
          </p>
        </v-card-text>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiExportVariant, mdiMagnify, mdiRefresh } from '@mdi/js'
import { mapFormHandlers } from '@/helpers/forms'

export default {
  name: 'OperatorReport',
  data: () => ({
    icons: { mdiExportVariant, mdiMagnify, mdiRefresh },
    tab: 0,
    search: '',
    expeditionItems: [
      { text: 'С доставкой', value: 'delivery' },
      { text: 'На вынос', value: 'pickup' },
    ],
  }),
  computed: {
    ...mapFormHandlers('operatorReport', [
      'filters.from',
      'filters.to',
      'filters.statusIds',
      'filters.query',
      'filters.isLate',
      'filters.restaurantId',
      'filters.expeditionType',
      'filters.operatorName',
      'filters.source',
    ]),
    ...mapState('operatorReport', {
      rows: 'rows',
      summary: 'summary',
      totals: 'totals',
      statuses: 'statuses',
      restaurants: 'restaurants',
      operators: 'operators',
      sources: 'sources',
      truncated: 'truncated',
      loading: 'loading',
      downloading: 'downloading',
      loaded: 'loaded',
      error: 'error',
    }),
    periodChosen() {
      return Boolean(this.filters.from && this.filters.to)
    },
    statusItems() {
      return this.statuses.map(status => ({ text: status.name, value: status.id }))
    },
    restaurantItems() {
      return this.restaurants.map(restaurant => ({ text: restaurant.name, value: restaurant.id }))
    },
    detailedHeaders() {
      return [
        { text: 'Дата/время создания заказа', value: 'createdAt' },
        { text: 'Номер заказа', value: 'orderId' },
        { text: 'Статус заказа', value: 'status' },
        { text: 'Фио оператора', value: 'operator' },
        { text: 'Филиал', value: 'restaurant' },
        { text: 'Тип заказа', value: 'orderType' },
        { text: 'Сумма заказа (до скидок)', value: 'orderAmount', align: 'end' },
        { text: 'Сумма по оплате (с учётом скидок)', value: 'paidAmount', align: 'end' },
        { text: 'Источник заказа', value: 'source' },
        { text: 'Последний оператор', value: 'lastOperator' },
      ]
    },
    summaryHeaders() {
      return [
        { text: 'Фио оператора', value: 'operator' },
        { text: 'Кол-во заказов', value: 'ordersCount', align: 'end' },
        { text: 'Сумма заказа (до скидок)', value: 'orderAmount', align: 'end' },
        { text: 'Сумма по оплате (со скидкой)', value: 'paidAmount', align: 'end' },
      ]
    },
    summaryTotals() {
      return this.summary.reduce((total, row) => ({
        ordersCount: total.ordersCount + row.ordersCount,
        orderAmount: total.orderAmount + row.orderAmount,
        paidAmount: total.paidAmount + row.paidAmount,
      }), { ordersCount: 0, orderAmount: 0, paidAmount: 0 })
    },
  },
  created() {
    document.title = 'Отчёт по операторам'
  },
  methods: {
    ...mapActions('operatorReport', { fetchReport: 'fetch', downloadReport: 'download' }),
    async refresh() {
      await this.fetchReport()
    },
    async download() {
      await this.downloadReport()
    },
    formatDate(value) {
      if (!value) return ''

      return new Date(value).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatMoney(value) {
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0)
    },
  },
}
</script>

<style lang="scss" scoped>
#operator-report {
  .operator-report-search {
    max-width: 20rem;
  }
}
</style>
